import React from "react";
import { toast } from "react-toastify";

/**
 * Fire a toast with an inline "Undo" action.
 *
 * Used for low-severity, reversible actions (e.g. removing a section from a
 * module) where a blocking "Are you sure?" confirm would add friction to a
 * routine editing flow. The toast gives one-click recovery for misclicks
 * without taxing the common (intentional) case.
 *
 * `closeOnClick: false` so a stray click on the toast body doesn't dismiss it
 * before the user can reach Undo. `onUndo` runs, then the toast closes.
 *
 * Uses toast.success (not the bare toast()) so the progress bar / accent is a
 * solid success green — the default toast type renders a multi-color gradient
 * progress bar.
 *
 * Returns the toast id so callers can dismiss a still-pending Undo toast when
 * its context goes away (e.g. the editor is closed) — a stale Undo can no
 * longer reliably recover the action.
 */
export function showUndoToast(message, onUndo) {
  return toast.success(
    ({ closeToast }) => (
      <div className="flex items-center justify-between gap-4">
        <span>{message}</span>
        <button
          type="button"
          onClick={() => {
            onUndo();
            closeToast();
          }}
          className="font-semibold text-violet-700 hover:text-violet-800 shrink-0"
        >
          Undo
        </button>
      </div>
    ),
    { autoClose: 6000, closeOnClick: false }
  );
}
